import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../generalMaster/GeneralMaster.scss";
import DataSearch from "../../utils/DataSearch";
import DataTable from "../../components/common/DataTable";
import SaveSuccessModel from "../../components/common/SaveSuccessModel";
import ErrorMessage from "../../components/common/ErrorMessage";
import UnSaveModel from "../../components/common/UnSaveModel";
import urls from "../../constants";
import { ErrorMessages, SuccessMessages } from "../../constants/MessageConstants";
import { GetListAsync, handleAuthError } from "../../providers/dataProvider";

const masterConfigMap = {
  Country: {
    resource: "generalcountry",
    endpoint: urls.GENERAL_COUNTRY_LIST_URL,
    labelTitle: "Country",
    labelKey: "name",
    placeholder: "Country name",
    columns: [
      { heading: "Country", value: "name", sortable: true },
      { heading: "Code", value: "code", sortable: true },
      { heading: "Status", value: "isActiveLabel", sortable: true },
    ],
    fields: [
      { name: "name", label: "Country Name", type: "text", required: true },
      { name: "code", label: "Code", type: "text", required: true },
    ],
    initialValues: {
      name: "",
      code: "",
      isActive: true,
    },
  },
  Currency: {
    resource: "generalcurrency",
    endpoint: urls.GENERAL_CURRENCY_LIST_URL,
    labelTitle: "Currency",
    labelKey: "currencyName",
    placeholder: "Currency name",
    columns: [
      { heading: "Currency", value: "currencyName", sortable: true },
      { heading: "Short Code", value: "shortCode", sortable: true },
      { heading: "Conversion Rate", value: "conversionRate", sortable: true },
      { heading: "Default", value: "isDefaultLabel", sortable: true },
    ],
    fields: [
      { name: "currencyName", label: "Currency Name", type: "text", required: true },
      { name: "shortCode", label: "Short Code", type: "text", required: true },
      {
        name: "conversionRate",
        label: "Conversion Rate",
        type: "number",
        required: true,
        step: "0.0001",
      },
      { name: "isDefault", label: "Default Currency", type: "checkbox" },
    ],
    initialValues: {
      currencyName: "",
      shortCode: "",
      conversionRate: "",
      companyId: "",
      isDefault: false,
    },
  },
  Documents: {
    resource: "generaldocumentTypes",
    endpoint: urls.GENERAL_DOCUMENT_LIST_URL,
    labelTitle: "Document",
    labelKey: "documentName",
    placeholder: "Document name",
    columns: [
      { heading: "Document", value: "documentName", sortable: true },
      { heading: "Date Validation", value: "dateValidationLabel", sortable: true },
      { heading: "Status", value: "isActiveLabel", sortable: true },
    ],
    fields: [
      { name: "documentName", label: "Document Name", type: "text", required: true },
      { name: "dateValidation", label: "Date Validation", type: "checkbox" },
    ],
    initialValues: {
      documentName: "",
      dateValidation: false,
      isActive: true,
    },
  },
  Designation: {
    resource: "generaldesignation",
    endpoint: urls.GENERAL_DESIGNATION_LIST_URL,
    labelTitle: "Designation",
    labelKey: "designationName",
    placeholder: "Designation name",
    columns: [
      { heading: "Designation", value: "designationName", sortable: true },
      { heading: "Hierarchy", value: "hierarchy", sortable: true },
      { heading: "Status", value: "isActiveLabel", sortable: true },
    ],
    fields: [
      { name: "designationName", label: "Designation Name", type: "text", required: true },
      { name: "hierarchy", label: "Hierarchy", type: "number", required: true },
    ],
    initialValues: {
      designationName: "",
      hierarchy: "",
      isActive: true,
    },
  },
  Department: {
    resource: "generaldepartment",
    endpoint: urls.GENERAL_DEPARTMENT_LIST_URL,
    labelTitle: "Department",
    labelKey: "departmentName",
    placeholder: "Department name",
    columns: [
      { heading: "Department", value: "departmentName", sortable: true },
      { heading: "Status", value: "isActiveLabel", sortable: true },
    ],
    fields: [{ name: "departmentName", label: "Department Name", type: "text", required: true }],
    initialValues: { departmentName: "", isActive: true },
  },
};
const userProfile = JSON.parse(localStorage.getItem("userProfile"));
console.log('userProfile',userProfile)
const getRowId = (row) => row?.id ?? row?.__clientId ?? "";

const buildClientId = () => `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const extractRows = (payload) => {
  if (Array.isArray(payload)) return payload;

  const candidates = [
    payload?.data,
    payload?.items,
    payload?.result,
    payload?.rows,
    payload?.value,
    payload?.data?.data,
    payload?.data?.items,
    payload?.data?.result,
    payload?.data?.rows,
    payload?.data?.value,
    payload?.$values,
    payload?.data?.$values,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  return [];
};

const normalizeRow = (masterKey, row = {}) => {
  const copy = { ...row };

  if (masterKey === "Country") {
    copy.name = row?.name ?? row?.countryName ?? row?.country ?? "";
    copy.code = row?.code ?? row?.shortCode ?? row?.countryCode ?? "";
    copy.isActive = row?.isActive ?? true;
  }

  if (masterKey === "Currency") {
  copy.currencyName = row?.currencyName ?? row?.name ?? "";
  copy.shortCode = row?.shortCode ?? row?.code ?? "";
  copy.conversionRate = row?.conversionRate ?? "";

  copy.isDefault =
    row?.isDefault === true ||
    row?.isDefault === 1 ||
    row?.isDefault === "1" ||
    row?.isDefault === "true";

  copy.isDefaultLabel = copy.isDefault ? "Yes" : "No";

  copy.companyId = row?.companyId ?? userProfile?.userCompanyId ?? null;
}

  if (masterKey === "Documents") {
    copy.documentName = row?.documentName ?? row?.name ?? "";
    copy.dateValidation = Boolean(row?.dateValidation);
    copy.dateValidationLabel = copy.dateValidation ? "Yes" : "No";
    copy.isActive = row?.isActive ?? true;
  }

  if (masterKey === "Designation") {
    copy.designationName = row?.designationName ?? row?.name ?? "";
    copy.hierarchy = row?.hierarchy ?? "";
    copy.isActive = row?.isActive ?? true;
  }

  if (masterKey === "Department") {
    copy.departmentName = row?.departmentName ?? row?.name ?? "";
    copy.isActive = row?.isActive ?? true;
  }

  copy.isActiveLabel = copy.isActive ? "Active" : "Inactive";

  return copy;
};

const normalizeRows = (rows = [], masterKey) => rows.map((row) => normalizeRow(masterKey, row));

const getCurrencyDefaultFlag = (row = {}) =>
  row?.isDefault === true ||
  row?.isDefault === 1 ||
  row?.isDefault === "1" ||
  row?.isDefault === "true";

const getCurrencyDisplayName = (row = {}) =>
  row?.currencyName ?? row?.name ?? row?.shortCode ?? "This currency";

const buildFormState = (masterKey, row = null) => {
  const config = masterConfigMap[masterKey] || masterConfigMap.Country;
  const nextState = { ...config.initialValues };

  config.fields.forEach((field) => {
    if (field.type === "checkbox") {
      nextState[field.name] = Boolean(row?.[field.name]);
      return;
    }

    nextState[field.name] = row?.[field.name] ?? "";
  });

  if (row?.id) nextState.id = row.id;
  if (row?.__clientId) nextState.__clientId = row.__clientId;

  return nextState;
};

const stripPayload = (masterKey, values) => {
  const payload = { ...values };
  delete payload.id;
  delete payload.__clientId;

  if (masterKey === "Country") {
    payload.name = payload.name?.trim();
    payload.code = payload.code?.trim();
  }

  if (masterKey === "Currency") {
    payload.currencyName = payload.currencyName?.trim();
    payload.shortCode = payload.shortCode?.trim();
    payload.conversionRate =
      payload.conversionRate === "" ? "" : Number(payload.conversionRate);
    payload.isDefault = Boolean(payload.isDefault);
    payload.companyId = userProfile?.userCompanyId ?? payload.companyId ?? null;
  }

  if (masterKey === "Documents") {
    payload.documentName = payload.documentName?.trim();
    payload.dateValidation = Boolean(payload.dateValidation);
  }

  if (masterKey === "Designation") {
    payload.designationName = payload.designationName?.trim();
    payload.hierarchy = payload.hierarchy === "" ? "" : Number(payload.hierarchy);
  }

  if (masterKey === "Department") {
    payload.departmentName = payload.departmentName?.trim();
  }

  payload.isActive = Boolean(payload.isActive);

  return payload;
};

const isSameRow = (masterKey, a = {}, b = {}) => {
  const left = stripPayload(masterKey, a);
  const right = stripPayload(masterKey, b);
  return JSON.stringify(left) === JSON.stringify(right);
};

const EditGeneralMaster = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState("Country");
  const [listData, setListData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [filteredResults, setFilterResult] = useState([]);
  const [filterColumn, setFilterColumn] = useState([]);
  const [search, setSearch] = useState("");
  const [formValues, setFormValues] = useState(buildFormState("Country"));
  const [editingKey, setEditingKey] = useState(null);
  const [removedRows, setRemovedRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createMsg, setCreateMsg] = useState(null);
  const [successShow, setSuccessShow] = useState(false);
  const [errorShow, setErrorShow] = useState({ show: false, errorMessage: "" });
  const [showDefaultCurrencyModal, setShowDefaultCurrencyModal] = useState(false);
  const [defaultCurrencyMessage, setDefaultCurrencyMessage] = useState("");
  const [defaultCurrencyPreviousValue, setDefaultCurrencyPreviousValue] = useState(false);
  const activeLoadIdRef = useRef(0);

  const initialSelected = useMemo(() => {
    const selectedFromState = location?.state?.row;
    return selectedFromState && masterConfigMap[selectedFromState]
      ? selectedFromState
      : "Country";
  }, [location?.state?.row]);

  const currentConfig = useMemo(
    () => masterConfigMap?.[selected] || masterConfigMap.Country,
    [selected]
  );

  const headerTitle = currentConfig?.labelTitle || selected;

  const currentRows = useMemo(
    () => normalizeRows(listData, selected),
    [listData, selected]
  );

  const currentOriginalRows = useMemo(
    () => normalizeRows(originalData, selected),
    [originalData, selected]
  );

  const masterEndpoint = currentConfig?.endpoint;

  const loadMasterList = async (masterKey = selected) => {
    const config = masterConfigMap?.[masterKey] || masterConfigMap.Country;
    const loadId = ++activeLoadIdRef.current;
    setLoading(true);
    try {
      const result = await GetListAsync(config.resource);
      const rows = normalizeRows(extractRows(result?.data), masterKey);
      if (loadId !== activeLoadIdRef.current) return;
      setOriginalData(rows);
      setListData(rows);
      setRemovedRows([]);
      setEditingKey(null);
      setFormValues(buildFormState(masterKey));
      setShowDefaultCurrencyModal(false);
      setDefaultCurrencyMessage("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelected(initialSelected);
  }, [initialSelected]);

  useEffect(() => {
    setFilterColumn(currentConfig?.columns || []);
  }, [currentConfig]);

  useEffect(() => {
    setCreateMsg(null);
    loadMasterList(selected);
  }, [selected]);

  useEffect(() => {
    if (!search?.trim()) {
      setFilterResult(currentRows);
      return;
    }

    setFilterResult(DataSearch(currentRows, search));
  }, [currentRows, search]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("pathfinderHeaderMetaChange", {
        detail: {
          pathname: location.pathname,
          title: headerTitle,
          backTarget: {
            pathname: "/general-master",
          },
        },
      })
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent("pathfinderHeaderMetaChange", {
          detail: {
            pathname: location.pathname,
            clear: true,
          },
        })
      );
    };
  }, [location.pathname, headerTitle]);

  const getFieldValue = (name) => formValues?.[name] ?? "";

  const updateField = (fieldName, value, fieldType) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: fieldType === "checkbox" ? Boolean(value) : value,
    }));
    if (error) setError("");
  };

  const validateForm = () => {
    for (const field of currentConfig.fields) {
      if (field.required) {
        const value = formValues?.[field.name];
        if (
          value === undefined ||
          value === null ||
          (typeof value === "string" && value.trim() === "")
        ) {
          return `${field.label} is required`;
        }
      }

      if (field.type === "number" && formValues?.[field.name] !== "") {
        const numericValue = Number(formValues?.[field.name]);
        if (Number.isNaN(numericValue)) {
          return `${field.label} must be a valid number`;
        }
      }
    }

    if (selected === "Country" && !formValues?.code?.trim()) {
      return "Code is required";
    }

    if (formValues?.isActive && (formValues?.isActive === undefined || formValues?.isActive === null)) {
      return "Status is required";
    }

    return "";
  };
  const buildDraftRow = () => {
    const payload = stripPayload(selected, formValues);
    const nextRow = {
      ...payload,
      __clientId:
        editingKey && String(editingKey).startsWith("tmp-") ? editingKey : editingKey || buildClientId(),
    };

    if (editingKey && !String(editingKey).startsWith("tmp-")) {
      nextRow.id = editingKey;
    }

    if (selected === "Documents") {
      nextRow.dateValidationLabel = nextRow.dateValidation ? "Yes" : "No";
    }

    return normalizeRow(selected, nextRow);
  };

  const handleAddToList = () => {
    const validationMessage = validateForm();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    const nextRow = buildDraftRow();
    const nextKey = getRowId(nextRow);

    const duplicateExists = listData.some((item) => {
      if (getRowId(item) === nextKey) return false;
      if (selected === "Country") return item?.name?.trim() === nextRow?.name?.trim();
      if (selected === "Currency")
        return item?.currencyName?.trim() === nextRow?.currencyName?.trim();
      if (selected === "Documents")
        return item?.documentName?.trim() === nextRow?.documentName?.trim();
      if (selected === "Designation")
        return item?.designationName?.trim() === nextRow?.designationName?.trim();
      return item?.departmentName?.trim() === nextRow?.departmentName?.trim();
    });

    if (duplicateExists) {
      setError(`${headerTitle} already exists in the list`);
      return;
    }

    setListData((prev) => {
      if (editingKey) {
        return prev.map((item) =>
          String(getRowId(item)) === String(editingKey) ? nextRow : item
        );
      }

      return [...prev, nextRow];
    });

    if (editingKey && String(editingKey).startsWith("tmp-")) {
      setRemovedRows((prev) =>
        prev.filter((item) => String(getRowId(item)) !== String(editingKey))
      );
    }

    setEditingKey(null);
    setFormValues(buildFormState(selected));
    setError("");
  };

  const handleEdit = (item) => {
    const key = getRowId(item);
    setEditingKey(key);
    setFormValues(buildFormState(selected, item));
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = (item) => {
    const key = getRowId(item);
    setListData((prev) => prev.filter((row) => getRowId(row) !== key));

    if (item?.id && !String(item.id).startsWith("tmp-")) {
      setRemovedRows((prev) => {
        if (prev.some((row) => row.id === item.id)) return prev;
        return [...prev, item];
      });
    }

    if (editingKey === key) {
      setEditingKey(null);
      setFormValues(buildFormState(selected));
    }
  };

  const requestMaster = async (method, url, payload = null) => {
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
      body: payload ? JSON.stringify(payload) : undefined,
    });

    if (response.status === 401 || response.status === 403) {
      handleAuthError();
      throw new Error("Unauthorized");
    }

    if (response.status === 204) return {};

    let data = {};
    try {
      data = await response.json();
    } catch (jsonError) {
      data = {};
    }

    if (!response.ok) {
      const message =
        data?.errors?.[0]?.message ||
        data?.message ||
        `Request failed with status ${response.status}`;
      throw new Error(message);
    }

    return data;
  };

  const handleSave = async () => {
    setError("");
    setSaving(true);
    setCreateMsg(null);

    const originalMap = new Map(
      currentOriginalRows.map((row) => [String(getRowId(row)), row])
    );
    const activeRows = currentRows.filter(
      (row) => !String(getRowId(row)).startsWith("tmp-")
    );
    const newRows = currentRows.filter((row) => String(getRowId(row)).startsWith("tmp-") || !row?.id);
    const changedRows = activeRows.filter((row) => {
      const original = originalMap.get(String(getRowId(row)));
      return original ? !isSameRow(selected, row, original) : true;
    });

    try {
      for (const removedRow of removedRows) {
        if (!removedRow?.id || String(removedRow.id).startsWith("tmp-")) continue;
        await requestMaster("DELETE", `${masterEndpoint}/${removedRow.id}`);
      }

      for (const row of [...changedRows, ...newRows]) {
        const payload = stripPayload(selected, row);
        if (row?.id && !String(row.id).startsWith("tmp-")) {
          await requestMaster("PATCH", `${masterEndpoint}/${row.id}`, payload);
        } else {
          await requestMaster("POST", masterEndpoint, payload);
        }
      }

      const hasCreates = newRows.length > 0;
      const hasUpdates = changedRows.length > 0;
      const hasDeletes = removedRows.length > 0;
      const successMessage =
        hasCreates && !hasUpdates && !hasDeletes
          ? SuccessMessages.CREATED(headerTitle)
          : hasDeletes && !hasCreates && !hasUpdates
            ? SuccessMessages.DELETED(headerTitle)
            : SuccessMessages.UPDATED(headerTitle);

      setCreateMsg({
        status: urls.SUCCESS_STATUS,
        message: successMessage,
        successData: {},
      });
      setSuccessShow(true);
      await loadMasterList(selected);
    } catch (saveError) {
      const failureMessage = ErrorMessages.UPDATE_ERROR(headerTitle);
      setCreateMsg({
        status: 400,
        message: failureMessage,
        successData: saveError?.message || "Save failed",
      });
      setErrorShow({
        show: true,
        errorMessage: {
          errors: [
            {
              message: saveError?.message || failureMessage,
            },
          ],
        },
      });
    } finally {
      setSaving(false);
    }
  };

  const clearSearch = () => {
    setSearch("");
  };

  const clearForm = () => {
    setEditingKey(null);
    setFormValues(buildFormState(selected));
    setError("");
  };

  const resetAllChanges = () => {
    setListData(originalData);
    setRemovedRows([]);
    setEditingKey(null);
    setFormValues(buildFormState(selected));
    setSearch("");
    setError("");
    setCreateMsg(null);
    setSuccessShow(false);
    setErrorShow({ show: false, errorMessage: "" });
    setShowDefaultCurrencyModal(false);
    setDefaultCurrencyMessage("");
  };

  const SuccessDoneClick = () => {
    setSuccessShow(false);
    setCreateMsg(null);
  };

  const ErrorDoneClick = () => {
    setErrorShow({ show: false, errorMessage: "" });
    setCreateMsg(null);
  };

  return (
    <div className="container-fluid edit-general-master-sec">
      <div className="card header-card">
        <div className="card-body">
          <div className="card general-info-card mb-2">
            <div className="card-header">
              <h2>{editingKey ? `Edit ${headerTitle}` : `Add ${headerTitle}`}</h2>
            </div>
            <div className="card-body bg-white">
              {error && <div className="alert alert-danger mb-3 py-2">{error}</div>}
              <div className="row g-3 justify-content-between">
                {currentConfig.fields.map((field) => (
                  <div
                    key={field.name}
                    className="col-xxl-4 col-xl-4 col-lg-4 col-md-6 col-sm-12 col-xs-12"
                  >
                    {field.type === "checkbox" ? (
                      <div className="form-check mt-4 pt-2">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={field.name}
                          checked={Boolean(getFieldValue(field.name))}
                          onChange={(e) => {
                            if (selected === "Currency" && field.name === "isDefault" && e.target.checked) {
                              setDefaultCurrencyPreviousValue(Boolean(getFieldValue(field.name)));
                              const existingDefaultRow = listData.find(
                                (item) =>
                                  getCurrencyDefaultFlag(item) &&
                                  String(getRowId(item)) !== String(editingKey || "")
                              );

                              if (existingDefaultRow) {
                                setDefaultCurrencyMessage(
                                  `${getCurrencyDisplayName(existingDefaultRow)} is already set as the default currency. Do you want to change the default currency?`
                                );
                                setShowDefaultCurrencyModal(true);
                                updateField(field.name, true, field.type);
                                return;
                              }
                            }

                            updateField(field.name, e.target.checked, field.type);
                          }}
                        />
                        <label className="form-check-label" htmlFor={field.name}>
                          {field.label}
                        </label>
                      </div>
                    ) : (
                      <div>
                        <label className="mb-1">
                          {field.label}
                          {field.required ? <span className="mandatory">*</span> : null}
                        </label>
                        <input
                          type={field.type}
                          step={field.step}
                          className="form-control mb-2"
                          placeholder={field.label}
                          value={getFieldValue(field.name)}
                          onChange={(e) => updateField(field.name, e.target.value, field.type)}
                        />
                      </div>
                    )}
                  </div>
                ))}
                <div className="col-xxl-4 col-xl-4 col-lg-4 col-md-12 col-sm-12 col-xs-12">
                  <div className="mt-32 mb-2">
                    <button className="btn" type="button" onClick={handleAddToList}>
                      {editingKey ? "Update" : "Add"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card country-list-card">
            <div className="card-body bg-white">
              <div className="row">
                <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12 mb-3">
                  <DataTable
                    filteredResults={filteredResults}
                    filterColumn={filterColumn}
                    handleEdit={handleEdit}
                    handleDelete={handleDelete}
                    moduleName="General List"
                    loading={loading}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-xxl-12 col-xl-12 col-lg-12 col-md-12 col-sm-12 col-xs-12 mt-3 mb-3">
              <div className="save-btns d-flex justify-content-end">
                <button className="btn cancel-btn" type="button" onClick={resetAllChanges}>
                  Cancel
                </button>
                <button className="btn save-btn" type="button" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ErrorMessage
        Show={errorShow?.show}
        errorMessage={errorShow?.errorMessage}
        ErrorDoneClick={ErrorDoneClick}
      />
      <UnSaveModel
        show={showDefaultCurrencyModal}
        setShow={setShowDefaultCurrencyModal}
        title="Change Default Currency"
        message={defaultCurrencyMessage}
        cancelText="Cancel"
        confirmText="Change"
        onCancel={() => {
          setShowDefaultCurrencyModal(false);
          setDefaultCurrencyMessage("");
          setFormValues((prev) => ({
            ...prev,
            isDefault: defaultCurrencyPreviousValue,
          }));
        }}
        onConfirm={() => {
          setShowDefaultCurrencyModal(false);
          setDefaultCurrencyMessage("");
        }}
      />
      <SaveSuccessModel
        show={successShow}
        createMsg={createMsg}
        setShow={setSuccessShow}
        SuccessDoneClick={SuccessDoneClick}
      />
    </div>
  );
};

export default EditGeneralMaster;
